// BlurDot shader adapted from BlurDotBg visual (2D compositing of multiple blurred circles)
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_seed;

uniform float u_ratio; // canvas aspect ratio w/h

uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;

uniform float u_p0_s;
uniform float u_p1_s;
uniform float u_p2_s;
uniform float u_p3_s;

uniform float u_p0x; uniform float u_p0y;
uniform float u_p1x; uniform float u_p1y;
uniform float u_p2x; uniform float u_p2y;
uniform float u_p3x; uniform float u_p3y;

float rand(vec2 co){ return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453 + u_seed); }

vec4 blurDot(vec3 color, vec2 st, vec2 pos, float inner, float outer) {
    float pct = distance(st, pos);
    float alpha = 1.0 - smoothstep(inner, outer, pct);
    return vec4(color, alpha);
}

vec3 blendColor(vec3 a, vec3 b, float t) {
    return a * (1.05 - t) + b * t;
}

void main(){
    vec2 st = gl_FragCoord.xy / u_resolution;
    // normalize to -0.5..+0.5 like background version
    float ratio = (u_ratio > 0.0) ? u_ratio : (u_resolution.x / max(1.0, u_resolution.y));
    st.x *= ratio; st.x -= ratio * 0.5; st.y -= 0.5;

    // Fallback defaults if uniforms are not provided by props
    float p0s = (u_p0_s <= 0.0) ? 0.9 : u_p0_s;
    float p1s = (u_p1_s <= 0.0) ? 0.9 : u_p1_s;
    float p2s = (u_p2_s <= 0.0) ? 0.9 : u_p2_s;
    float p3s = (u_p3_s <= 0.0) ? 0.9 : u_p3_s;

    float p0x = (u_p0x == 0.0 && u_p0y == 0.0) ? (-ratio * 0.3) : u_p0x;
    float p0y = (u_p0x == 0.0 && u_p0y == 0.0) ? (-0.2) : u_p0y;
    float p1x = (u_p1x == 0.0 && u_p1y == 0.0) ? (-ratio * 0.15) : u_p1x;
    float p1y = (u_p1x == 0.0 && u_p1y == 0.0) ? (0.2) : u_p1y;
    float p2x = (u_p2x == 0.0 && u_p2y == 0.0) ? (ratio * 0.15) : u_p2x;
    float p2y = (u_p2x == 0.0 && u_p2y == 0.0) ? (-0.2) : u_p2y;
    float p3x = (u_p3x == 0.0 && u_p3y == 0.0) ? (ratio * 0.3) : u_p3x;
    float p3y = (u_p3x == 0.0 && u_p3y == 0.0) ? (0.2) : u_p3y;

    vec3 color = vec3(1.0);
    vec4 d0 = blurDot(u_color_0, st, vec2(p0x, p0y), 0.1, p0s);
    vec4 d1 = blurDot(u_color_1, st, vec2(p1x, p1y), 0.1, p1s);
    vec4 d2 = blurDot(u_color_2, st, vec2(p2x, p2y), 0.1, p2s);
    vec4 d3 = blurDot(u_color_3, st, vec2(p3x, p3y), 0.1, p3s);

    color = blendColor(color, d3.rgb, d3.a);
    color = blendColor(color, d2.rgb, d2.a);
    color = blendColor(color, d1.rgb, d1.a);
    color = blendColor(color, d0.rgb, d0.a);

    // subtle pulsing based on time for dynamism when animated
    float t = u_time * u_speed;
    float pulse = 0.03 * sin(t * 0.7);
    color += pulse;
    gl_FragColor = vec4(color, 1.0);
}
`;

const shader = {
  name: 'BlurDot',
  shader: { frag },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 4,
      default: ['#ffd3a5', '#fd6585', '#4facfe', '#00f2fe'],
    },
    {
      key: 'u_ratio',
      type: 'number',
      label: 'Aspect',
      min: 0.1,
      max: 5,
      step: 0.01,
      default: 1.777,
    },
    {
      key: 'u_p0_s',
      type: 'number',
      label: 'Dot0 Size',
      min: 0.2,
      max: 1.2,
      step: 0.01,
      default: 0.9,
    },
    {
      key: 'u_p1_s',
      type: 'number',
      label: 'Dot1 Size',
      min: 0.2,
      max: 1.2,
      step: 0.01,
      default: 0.9,
    },
    {
      key: 'u_p2_s',
      type: 'number',
      label: 'Dot2 Size',
      min: 0.2,
      max: 1.2,
      step: 0.01,
      default: 0.9,
    },
    {
      key: 'u_p3_s',
      type: 'number',
      label: 'Dot3 Size',
      min: 0.2,
      max: 1.2,
      step: 0.01,
      default: 0.9,
    },
    {
      key: 'u_p0x',
      type: 'number',
      label: 'Dot0 X',
      min: -1,
      max: 1,
      step: 0.01,
      default: -0.3,
    },
    {
      key: 'u_p0y',
      type: 'number',
      label: 'Dot0 Y',
      min: -1,
      max: 1,
      step: 0.01,
      default: -0.2,
    },
    {
      key: 'u_p1x',
      type: 'number',
      label: 'Dot1 X',
      min: -1,
      max: 1,
      step: 0.01,
      default: -0.15,
    },
    {
      key: 'u_p1y',
      type: 'number',
      label: 'Dot1 Y',
      min: -1,
      max: 1,
      step: 0.01,
      default: 0.2,
    },
    {
      key: 'u_p2x',
      type: 'number',
      label: 'Dot2 X',
      min: -1,
      max: 1,
      step: 0.01,
      default: 0.15,
    },
    {
      key: 'u_p2y',
      type: 'number',
      label: 'Dot2 Y',
      min: -1,
      max: 1,
      step: 0.01,
      default: -0.2,
    },
    {
      key: 'u_p3x',
      type: 'number',
      label: 'Dot3 X',
      min: -1,
      max: 1,
      step: 0.01,
      default: 0.3,
    },
    {
      key: 'u_p3y',
      type: 'number',
      label: 'Dot3 Y',
      min: -1,
      max: 1,
      step: 0.01,
      default: 0.2,
    },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 10, step: 0.1, default: 1 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: false },
  ],
  examples: [
    'dynamic-shader(BlurDot, colors=#ffd3a5,#fd6585,#4facfe,#00f2fe; u_ratio=1.5; animated=false)',
    'dynamic-shader(BlurDot, colors=#ff9966,#ff5e62,#222,#fff; u_p0_s=1.0; u_p3_s=0.8; animated=false)',
  ],
};

export default shader;
